import { Link } from "react-router-dom";
import config from "../config/index.js";
import { useWallet } from "../context/WalletContext.jsx";
import { FaqContent } from "./Faq.jsx";

/**
 * Help / Support — FAQ, a protocol-parameter reference table and key
 * contract addresses. Static, wallet-independent content.
 */
export default function Support() {
    // Guests (no wallet) get the full-bleed public chrome (no sidebar, no
    // padded <main>), which stretches this app-style page edge-to-edge and
    // looks off. When there's no connected wallet we add our own centered
    // max-width container + padding so it reads the same as the connected
    // view. Connected users already sit inside the padded app <main>, so we
    // leave the layout to that and add nothing extra.
    const { address } = useWallet();
    const guest = !address;

    const params = [
        ["Term length", `${config.protocol.fixedTermDays} days (3 years)`],
        ["Annual yield (Standard)", `${config.protocol.annualRateBps / 100}% simple`],
        ["Turbo net yield", "up to 27.5%"],
        ["Minimum deposit", `${config.protocol.minDepositUsdc} USDC`],
        ["Capital routing", "80% Vault / 10% $GILD buy / 9% Distribution Reserve / 1% Liquidity"],
        ["Early-exit penalty", `${config.protocol.earlyExitPenaltyBps / 100}% Standard / ~50% Turbo`],
        ["Max borrow LTV", `${config.protocol.maxLtvBps / 100}%`],
        ["Loan APR", `${config.protocol.loanAprBps / 100}%`],
        ["Liquidation threshold", `${config.protocol.liquidationThresholdBps / 100}%`],
        ["Yield coverage target", "~6 months"],
        ["Automated Reinvestment trigger", `${config.protocol.autoCompoundTriggerUsdc} USDC`],
        // §M6 (Jul 2026 client review) — royalty cut 5% → 2%. Driven off
        // config so this row and the FAQ can never drift from the on-chain
        // DepositNFT.ROYALTY_BPS again.
        ["NFT royalty", `${config.protocol.nftRoyaltyBps / 100}% (enforced on-chain)`]
    ];

    const contracts = [
        ["Bond", config.contracts.bond],
        ["Safe Vault", config.contracts.safeVault],
        ["Distribution Reserve", config.contracts.treasury],
        ["Deposit NFT", config.contracts.depositNft],
        ["Lending", config.contracts.lending],
        ["Turbo", config.contracts.turbo],
        ["Automated Reinvestment", config.contracts.autoCompound],
        ["Reinvestment", config.contracts.reinvestment]
    ].filter(([, addr]) => addr);

    return (
        <div
            className={`flex flex-col gap-3${
                guest ? " mx-auto w-full max-w-[1280px] px-4 py-6 sm:px-6 sm:py-8 lg:px-8" : ""
            }`}
        >

            {/* Parameters → Deployed Contracts → Need more help */}
            <div className="grid gap-3 lg:grid-cols-3">
                <div className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                        <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">
                            Protocol Parameters
                        </h3>
                        <dl className="mt-2 space-y-1.5 text-[10.5px]">
                            {params.map(([label, value]) => (
                                <div
                                    key={label}
                                    className="flex items-start justify-between gap-3 border-b border-white/5 pb-1.5 last:border-0"
                                >
                                    <dt className="text-[#e6c985]/70">{label}</dt>
                                    <dd className="text-right font-semibold text-white">{value}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>

                    {contracts.length > 0 ? (
                        <div className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                            <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">
                                Deployed Contracts ({config.network.name})
                            </h3>
                            <div className="mt-2 grid gap-1.5">
                                {contracts.map(([label, addr]) => (
                                    <a
                                        key={label}
                                        href={`${config.network.blockExplorerUrl}/address/${addr}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex items-center justify-between rounded-md bg-white/10 ring-1 ring-[#bfb67b] px-3 py-1.5 text-[10.5px] hover:bg-white/15 transition-colors"
                                    >
                                        <span className="font-semibold text-white">{label}</span>
                                        <span className="text-[#FBC206]">
                                            {addr.slice(0, 8)}…{addr.slice(-6)}
                                        </span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    <div className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                        <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">
                            Need more help?
                        </h3>
                        <p className="mt-1.5 text-[10.5px] leading-snug text-[#e6c985]/75">
                            Check live solvency on the health dashboard. The full FAQ is
                            embedded below — no need to leave this page.
                        </p>
                        <div className="mt-2.5 flex flex-col gap-1.5">
                            <Link
                                to="/protocol-health"
                                className="rounded-md bg-[#162e1f] ring-1 ring-[#bfb67b] px-3 py-1.5 text-[11px] font-semibold text-[#e6c985] hover:bg-[#1d3a27] transition-colors"
                            >
                                Protocol Health →
                            </Link>
                            <a
                                href="#support-faq"
                                className="rounded-md bg-[#162e1f] ring-1 ring-[#bfb67b] px-3 py-1.5 text-[11px] font-semibold text-[#e6c985] hover:bg-[#1d3a27] transition-colors"
                            >
                                Jump to the FAQ ↓
                            </a>
                        </div>
                    </div>
            </div>

            {/* §M6 (Jul 2026 client review) — "Can we just have the FAQ
                embedded in this page so they don't have to click out of it?"
                Same content and data model as /faq, rendered inline behind a
                disclosure so the parameter/contract cards stay above the fold. */}
            <section id="support-faq" className="scroll-mt-24">
                <FaqContent collapsedByDefault />
            </section>
        </div>
    );
}

